// Thin wrapper around the Google Identity Services (GSI) token
// flow. Returns an access token scoped to whatever the caller
// asks for; the token lives in memory only - never persisted to
// localStorage / cookies, never sent to InRange's backend. The
// caller passes it as an `Authorization: Bearer …` header on
// direct Gmail API calls.
//
// This is operator-side OAuth (the operator signs in to their own
// Google account to grant the v2 app read access to their Gmail);
// it is NOT the InRange API auth seam (which lives in `web/src/auth/`
// and is wired via Kinde).
import { GOOGLE_CLIENT_ID } from "./google-config";

// Minimal hand-rolled types for the bits of the GSI library we
// use. The npm `@types/google.accounts` package exists but is
// stale; ten lines of declare-global here is less moving-target.
declare global {
  interface Window {
    google?: {
      accounts: {
        oauth2: {
          initTokenClient: (config: {
            client_id: string;
            scope: string;
            callback: (resp: {
              access_token?: string;
              scope?: string;
              error?: string;
              error_description?: string;
            }) => void;
            error_callback?: (err: { type?: string }) => void;
          }) => {
            requestAccessToken: (opts?: { prompt?: string }) => void;
          };
        };
      };
      // Picker SDK shape - only what we actually call. Real types
      // are in @types/google.picker but it's stale; matching what
      // we use is less moving-target.
      picker: GooglePicker;
    };
    gapi?: {
      load: (lib: string, cb: () => void) => void;
    };
  }
}

interface GooglePicker {
  PickerBuilder: new () => PickerBuilder;
  DocsView: new (viewId?: unknown) => PickerDocsView;
  ViewId: { DOCS: unknown; FOLDERS: unknown };
  Feature: { MULTISELECT_ENABLED: unknown };
  Action: { PICKED: string; CANCEL: string };
}

export interface PickerDoc {
  id: string;
  name: string;
  mimeType: string;
  // Other fields set by Picker (sizeBytes / iconUrl / etc.) are
  // ignored; the recursive listing pulls metadata fresh from
  // drive.files.list anyway.
}

interface PickerBuilder {
  enableFeature: (f: unknown) => PickerBuilder;
  setOAuthToken: (t: string) => PickerBuilder;
  setDeveloperKey: (k: string) => PickerBuilder;
  addView: (v: unknown) => PickerBuilder;
  setCallback: (cb: (data: { action: string; docs?: PickerDoc[] }) => void) => PickerBuilder;
  build: () => { setVisible: (v: boolean) => void };
}

interface PickerDocsView {
  setIncludeFolders: (v: boolean) => PickerDocsView;
  setSelectFolderEnabled: (v: boolean) => PickerDocsView;
  setMimeTypes: (s: string) => PickerDocsView;
}

const GSI_SCRIPT_SRC = "https://accounts.google.com/gsi/client";
const GAPI_SCRIPT_SRC = "https://apis.google.com/js/api.js";

const loadedScripts = new Set<string>();

const loadScript = (src: string): Promise<void> =>
  new Promise((resolve, reject) => {
    if (loadedScripts.has(src) || document.querySelector(`script[src="${src}"]`)) {
      loadedScripts.add(src);
      resolve();
      return;
    }
    const tag = document.createElement("script");
    tag.src = src;
    tag.async = true;
    tag.defer = true;
    tag.onload = () => {
      loadedScripts.add(src);
      resolve();
    };
    tag.onerror = () => reject(new Error(`Failed to load ${src}`));
    document.head.appendChild(tag);
  });

export const ensureGsiLoaded = (): Promise<void> => loadScript(GSI_SCRIPT_SRC);

// Load the gapi loader script + the Picker library on top of it.
// Two separate loads because the picker module is requested via
// `gapi.load("picker", cb)` after the loader itself is ready.
// Memoised separately from GSI so a Gmail-only session doesn't
// pay the picker load cost.
let pickerReadyPromise: Promise<void> | null = null;
export const ensureGapiPickerLoaded = (): Promise<void> => {
  if (pickerReadyPromise) return pickerReadyPromise;
  pickerReadyPromise = (async () => {
    await loadScript(GAPI_SCRIPT_SRC);
    if (!window.gapi) {
      throw new GoogleAuthError("Google API loader (gapi) failed to register.");
    }
    await new Promise<void>((resolve) => {
      window.gapi!.load("picker", () => resolve());
    });
  })().catch((e) => {
    // On failure clear the memoised promise so a retry can re-run.
    pickerReadyPromise = null;
    throw e;
  });
  return pickerReadyPromise;
};

export class GoogleAuthError extends Error {
  // Set to `true` when the error is a benign user-initiated cancel
  // (the operator closed the consent popup, or the popup never
  // opened due to a pop-up blocker). Callers should treat these as
  // "silent abort" - clear progress state but don't surface a red
  // error box. Distinct from a hard origin/scope mismatch which
  // genuinely needs the operator's attention.
  isUserCancelled = false;
}

// Access tokens last ~1h in Google's grant; we expire ours a bit
// earlier to leave headroom for the API call latency that comes
// after `requestGoogleAccessToken` returns. Picked at 5 min - 90%
// of operator sessions are shorter than that, so the cache hit
// rate stays high without risking a 401 mid-call.
const TOKEN_EXPIRY_SAFETY_MS = 5 * 60 * 1000;
const TOKEN_TTL_MS = 60 * 60 * 1000 - TOKEN_EXPIRY_SAFETY_MS;

// sessionStorage so the cache survives a route transition / page
// reload within the tab, but vanishes on tab close. localStorage
// would persist across tabs but stretches the lifetime of an
// access token past where we have any insight into the operator's
// session (e.g. they share their laptop, sign out, etc.) - the
// security trade-off isn't worth the marginal UX gain.
const SESSION_STORAGE_PREFIX = "inrange-agents.google-token.";

interface CachedToken {
  token: string;
  scope: string;
  expires_at_ms: number;
}

const readCached = (key: string): CachedToken | null => {
  try {
    const raw = window.sessionStorage.getItem(SESSION_STORAGE_PREFIX + key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CachedToken;
    if (
      typeof parsed.token !== "string" ||
      typeof parsed.scope !== "string" ||
      typeof parsed.expires_at_ms !== "number"
    ) {
      return null;
    }
    if (parsed.expires_at_ms < Date.now()) return null;
    return parsed;
  } catch {
    // sessionStorage can throw in private-browsing modes / when
    // quota is exceeded. Either way we degrade gracefully to "no
    // cache" - the consent flow still works.
    return null;
  }
};

const writeCached = (key: string, value: CachedToken): void => {
  try {
    window.sessionStorage.setItem(
      SESSION_STORAGE_PREFIX + key,
      JSON.stringify(value),
    );
  } catch {
    // Best-effort; silently skip caching when sessionStorage is
    // unavailable.
  }
};

const clearCached = (key: string): void => {
  try {
    window.sessionStorage.removeItem(SESSION_STORAGE_PREFIX + key);
  } catch {
    // ignore
  }
};

// Request an OAuth access token for the given scopes.
//
// Token lifecycle:
//   1. Cache hit (sessionStorage, not expired, covers all
//      requested scopes) - return the cached token without any
//      Google round trip. This is the common case after the
//      operator has already consented once in this tab.
//   2. Cache miss - call GSI with `prompt: ""` (silent). If the
//      operator has previously granted these scopes on this
//      origin, GSI returns a fresh access token without UI.
//   3. Silent fails with access_denied / interaction_required /
//      missing scopes - retry with `prompt: "consent"` which
//      always shows the picker / consent dialog. This is what
//      runs the first time the operator visits, or after they've
//      revoked the grant.
//
// We force `prompt: "consent"` only when silent reuse genuinely
// can't satisfy the request - either because no prior grant exists
// or because GSI tells us interaction is required. Adding a new
// scope (e.g. Gmail on top of a previously-granted Drive token)
// flows through path 3 because the silent attempt won't return
// the missing scope and falls through to consent.
export const requestGoogleAccessToken = (scopes: string[]): Promise<string> => {
  const scopeKey = [...scopes].sort().join(" ");
  const cached = readCached(scopeKey);
  if (cached) return Promise.resolve(cached.token);

  return new Promise((resolve, reject) => {
    if (!window.google?.accounts?.oauth2) {
      reject(
        new GoogleAuthError(
          "Google Identity Services library not loaded - call ensureGsiLoaded() first.",
        ),
      );
      return;
    }
    const scopeStr = scopes.join(" ");

    let hasAttemptedConsent = false;

    const handleSuccess = (token: string): void => {
      writeCached(scopeKey, {
        token,
        scope: scopeStr,
        expires_at_ms: Date.now() + TOKEN_TTL_MS,
      });
      resolve(token);
    };

    const tokenClient = window.google.accounts.oauth2.initTokenClient({
      client_id: GOOGLE_CLIENT_ID,
      scope: scopeStr,
      callback: (resp) => {
        if (resp.error || !resp.access_token) {
          // Origin-mismatch errors arrive here (not error_callback)
          // when the OAuth client's Authorized JavaScript origins
          // allowlist doesn't include the current page's origin.
          // GSI sets resp.error to "access_denied" and
          // resp.error_description to something like
          // "Error 400: redirect_uri_mismatch" or similar. The
          // legacy showcase showed the operator a useless raw
          // string; surface a self-describing message that names
          // the fix (a Console-side allowlist edit) instead of a
          // specific person, since this repo is now driven by
          // John, Noaa, and Martin - see CLAUDE.md "Google sign-
          // in on QA envs" for the runbook.
          const errStr = resp.error_description ?? resp.error ?? "";

          // Silent request failed because consent is needed - flip
          // to prompt:consent and retry once before surfacing the
          // error. `interaction_required` is the GSI signal for
          // "no prior grant exists" on a silent call; access_denied
          // can also come from this path when the user has revoked.
          // Don't retry if we're already in the consent attempt.
          const needsConsent =
            !hasAttemptedConsent &&
            (resp.error === "interaction_required" ||
              resp.error === "access_denied" ||
              /interaction_required/i.test(errStr));
          if (needsConsent) {
            hasAttemptedConsent = true;
            tokenClient.requestAccessToken({ prompt: "consent" });
            return;
          }

          if (
            /origin|redirect_uri|access[_ ]denied/i.test(errStr) ||
            resp.error === "access_denied"
          ) {
            reject(
              new GoogleAuthError(
                "This deploy isn't authorised for Google sign-in. Add " +
                  "this page's origin to the Google OAuth client's " +
                  "Authorized JavaScript origins allowlist (a Google " +
                  "Cloud Console setting on the agentic-ai-operating-" +
                  "model project). See CLAUDE.md 'Google sign-in on " +
                  "QA envs' for the runbook.",
              ),
            );
            return;
          }
          reject(
            new GoogleAuthError(
              resp.error_description ?? resp.error ?? "OAuth request failed",
            ),
          );
          return;
        }
        const granted = (resp.scope ?? "").split(/\s+/);
        const missing = scopes.filter((s) => !granted.includes(s));
        if (missing.length > 0) {
          // Silent reuse returned a token but for the wrong scope
          // set (e.g. operator previously granted Drive, now we're
          // asking for Drive + Gmail). Retry once with consent.
          if (!hasAttemptedConsent) {
            hasAttemptedConsent = true;
            clearCached(scopeKey);
            tokenClient.requestAccessToken({ prompt: "consent" });
            return;
          }
          reject(
            new GoogleAuthError(
              `OAuth grant returned scopes [${granted.join(", ")}], missing ${missing.join(", ")}. ` +
                "Revoke previous access at https://myaccount.google.com/permissions and try again.",
            ),
          );
          return;
        }
        handleSuccess(resp.access_token);
      },
      error_callback: (err) => {
        // GSI's `error_callback` fires for OAuth flow setup
        // failures (popup couldn't open, popup closed, library
        // init failure). Origin-mismatch errors are NOT emitted
        // here despite what the legacy comment thread suggested -
        // they surface through the `callback` path with
        // resp.error set, handled above. Keep this branch to the
        // narrow set of types GSI actually emits.
        const t = err?.type ?? "";
        if (t === "popup_closed" || t === "popup_failed_to_open") {
          const e = new GoogleAuthError("Sign-in cancelled.");
          e.isUserCancelled = true;
          reject(e);
          return;
        }
        if (t === "idpiframe_initialization_failed") {
          reject(
            new GoogleAuthError(
              "Google sign-in couldn't initialise on this page. Often a " +
                "popup blocker or third-party-cookie restriction; try " +
                "again with the blocker disabled, or in an incognito " +
                "window with cookies allowed for google.com.",
            ),
          );
          return;
        }
        reject(new GoogleAuthError(t || "OAuth error"));
      },
    });
    // Try silent first; the callback flips to `prompt: "consent"`
    // on interaction_required / scope mismatch.
    tokenClient.requestAccessToken({ prompt: "" });
  });
};

// Module-scoped dedup so that when six concurrent workers all
// 401 simultaneously (Drive picker) they collapse onto a single
// consent popup. Keyed on the sorted scope string so a Gmail-only
// refresh doesn't reuse a Drive token grant (which would carry the
// wrong scopes and fail the scope-coverage check inside
// `requestGoogleAccessToken`).
//
// The promise is cleared in `finally` so that the next 401 wave
// (later token expiry) gets a fresh consent rather than reusing a
// stale promise.
const refreshInFlight = new Map<string, Promise<string>>();

export const refreshGoogleAccessToken = (scopes: string[]): Promise<string> => {
  const key = [...scopes].sort().join(" ");
  const existing = refreshInFlight.get(key);
  if (existing) return existing;
  // A 401 from the API means the cached token is stale on
  // Google's side (operator revoked, or the ~1h lifetime
  // elapsed). Wipe the sessionStorage entry so the next call
  // doesn't immediately try the same stale token again.
  clearCached(key);
  const p = (async () => {
    await ensureGsiLoaded();
    return requestGoogleAccessToken(scopes);
  })().finally(() => {
    refreshInFlight.delete(key);
  });
  refreshInFlight.set(key, p);
  return p;
};
